/**
 * Models for the round-2b mobs (witch, phantom, cat, horse/donkey, squid, fish, bat ...).
 * Real vanilla geometry (ModelPart boxes, pivots, UV offsets) over the real entity
 * sheets in public/mc/entity (model px space, y down, front -z, feet at y = 24).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type EntityMat } from './core';
import type { MobView } from '../../game/types';
import type { QuadPose } from './mobs';
import { DYE_RGB, type OwAnim } from './overworldModels';

/** Textured rig: pivot groups holding UV-mapped boxes over one vanilla sheet. */
export class Rig {
  readonly parts = new THREE.Group();
  readonly mats: EntityMat[] = [];
  readonly mat: EntityMat;
  constructor(env: EnvUniforms, sheet: string, private readonly tw: number, private readonly th: number,
    opts: { emissive?: boolean; doubleSided?: boolean } = {}) {
    this.mat = entityMaterial(env, sheetTexture(sheet), opts);
    this.mats.push(this.mat);
  }

  /** A pivot at `pos` (px) under `parent` (default: the root). */
  pivot(pos: [number, number, number], parent: THREE.Object3D = this.parts, rot: [number, number, number] = [0, 0, 0]): THREE.Group {
    const g = new THREE.Group();
    g.rotation.order = 'ZYX';
    g.position.set(pos[0], pos[1], pos[2]);
    g.rotation.set(rot[0], rot[1], rot[2]);
    parent.add(g);
    return g;
  }

  /** A cuboid with vanilla texOffs `tex` (px, relative to its pivot). */
  box(g: THREE.Object3D, tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): void {
    g.add(buildPart({ cubes: [{ tex, from, size, inflate, mirror }] }, this.tw, this.th, this.mat));
  }

  anim(animate: (v: MobView, q: QuadPose, headY: number, headX: number, age: number) => void): OwAnim {
    return { parts: this.parts, mats: this.mats, animate, dispose: () => { for (const m of this.mats) m.dispose(); } };
  }
}

const PI = Math.PI;

// ------------------------------------------------------------------- witch

/** WitchModel (VillagerModel + hat tiers + mole), 64x128 sheet. */
export function witchAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/witch/witch', 64, 128);
  const legR = r.pivot([-2, 12, 0]), legL = r.pivot([2, 12, 0]);
  r.box(legR, [0, 22], [-2, 0, -2], [4, 12, 4]);
  r.box(legL, [0, 22], [-2, 0, -2], [4, 12, 4], 0, true);
  const body = r.pivot([0, 0, 0]);
  r.box(body, [16, 20], [-4, 0, -3], [8, 12, 6]);
  r.box(body, [0, 38], [-4, 0, -3], [8, 18, 6], 0.5);
  const head = r.pivot([0, 0, 0]);
  r.box(head, [0, 0], [-4, -10, -4], [8, 10, 8]);
  const nose = r.pivot([0, -2, 0], head);
  r.box(nose, [24, 0], [-1, -1, -6], [2, 4, 2]);
  r.box(nose, [0, 0], [0, 3, -6.75], [1, 1, 1], -0.25);
  const hat = r.pivot([-5, -10.03125, -5], head);
  r.box(hat, [0, 64], [0, 0, 0], [10, 2, 10]);
  const hat2 = r.pivot([1.75, -4, 2], hat, [-0.05235988, 0, 0.02617994]);
  r.box(hat2, [0, 76], [0, 0, 0], [7, 4, 7]);
  const hat3 = r.pivot([1.75, -4, 2], hat2, [-0.10471976, 0, 0.05235988]);
  r.box(hat3, [0, 87], [0, 0, 0], [4, 4, 4]);
  const hat4 = r.pivot([1.75, -2, 2], hat3, [-0.20943951, 0, 0.10471976]);
  r.box(hat4, [0, 95], [0, 0, 0], [1, 2, 1], 0.25);
  const arms = r.pivot([0, 3, -1], r.parts, [-0.75, 0, 0]);
  r.box(arms, [44, 22], [-8, -2, -2], [4, 8, 4]);
  r.box(arms, [44, 22], [4, -2, -2], [4, 8, 4], 0, true);
  r.box(arms, [40, 38], [-4, 2, -2], [8, 4, 4]);
  return r.anim((v, q, headY, headX) => {
    head.rotation.set(headX, headY, 0);
    const s = q.limbSwing * 0.6662;
    legR.rotation.x = Math.cos(s) * 1.4 * q.limbAmt * 0.5;
    legL.rotation.x = Math.cos(s + PI) * 1.4 * q.limbAmt * 0.5;
    // drinking / throwing: the folded arms lift toward the face
    arms.rotation.x = v.heldItem !== 0 ? -2.0 : v.attackAnim > 0 ? -0.75 - Math.sin(v.attackAnim * PI) * 1.2 : -0.75;
  });
}

// ----------------------------------------------------------------- phantom

/** PhantomModel (64x64) built once per layer: the body sheet and the emissive eyes sheet. */
function phantomRig(env: EnvUniforms, sheet: string, emissive: boolean) {
  const r = new Rig(env, sheet, 64, 64, { emissive });
  const body = r.pivot([0, 2.6, 0], r.parts, [-0.1, 0, 0]);
  r.box(body, [0, 8], [-3, -2, -8], [5, 3, 9]);
  const tailBase = r.pivot([0, -2, 1], body);
  r.box(tailBase, [3, 20], [-2, 0, 0], [3, 2, 6]);
  const tailTip = r.pivot([0, 0.5, 6], tailBase);
  r.box(tailTip, [4, 29], [-1, 0, 0], [1, 1, 6]);
  const wingL = r.pivot([2, -2, -8], body, [0, 0, 0.1]);
  r.box(wingL, [23, 12], [0, 0, 0], [6, 2, 9]);
  const wingL2 = r.pivot([6, 0, 0], wingL, [0, 0, 0.1]);
  r.box(wingL2, [16, 24], [0, 0, 0], [13, 1, 9]);
  const wingR = r.pivot([-3, -2, -8], body, [0, 0, -0.1]);
  r.box(wingR, [23, 12], [-6, 0, 0], [6, 2, 9], 0, true);
  const wingR2 = r.pivot([-6, 0, 0], wingR, [0, 0, -0.1]);
  r.box(wingR2, [16, 24], [-13, 0, 0], [13, 1, 9], 0, true);
  const head = r.pivot([0, 1, -7], body, [0.2, 0, 0]);
  r.box(head, [0, 0], [-4, -2, -5], [7, 3, 5]);
  return { r, body, tailBase, tailTip, wingL, wingL2, wingR, wingR2, head };
}

export function phantomAnim(env: EnvUniforms): OwAnim {
  const layers = [phantomRig(env, 'entity/phantom/phantom', false), phantomRig(env, 'entity/phantom/phantom_eyes', true)];
  const parts = new THREE.Group();
  for (const l of layers) parts.add(l.r.parts);
  const D = PI / 180;
  return {
    parts, mats: layers.flatMap((l) => l.r.mats),
    animate(v, _q, headY, headX, age) {
      // vanilla: f = (flapOffset + age) * 7.448 deg, wings sweep 16 deg, tail 5 +- 5 deg
      const f = (v.id % 97 + age) * 7.448451 * D * 0.05;
      for (const l of layers) {
        l.wingL.rotation.z = Math.cos(f) * 16 * D; l.wingL2.rotation.z = Math.cos(f) * 16 * D;
        l.wingR.rotation.z = -l.wingL.rotation.z; l.wingR2.rotation.z = -l.wingL2.rotation.z;
        l.tailBase.rotation.x = -(5 + Math.cos(f * 2) * 5) * D; l.tailTip.rotation.x = l.tailBase.rotation.x;
        l.head.rotation.x = 0.2 + headX * 0.5; l.head.rotation.y = headY * 0.5;
      }
    },
    dispose() { for (const l of layers) for (const m of l.r.mats) m.dispose(); },
  };
}

// --------------------------------------------------------------------- cat

/** Vanilla cat variants (texture file under entity/cat/), picked by mob id. */
const CAT_SKINS = ['cat_tabby', 'cat_black', 'cat_red', 'cat_siamese', 'cat_british_shorthair', 'cat_calico',
  'cat_persian', 'cat_ragdoll', 'cat_white', 'cat_jellie', 'cat_all_black'] as const;

/** CatModel / OcelotModel (64x32) built once per layer: the coat sheet and the tamed cat's collar. */
function catRig(env: EnvUniforms, sheet: string, inflate: number) {
  const r = new Rig(env, sheet, 64, 32);
  const head = r.pivot([0, 15, -9]);
  r.box(head, [0, 0], [-2.5, -2, -3], [5, 4, 5], inflate);
  r.box(head, [0, 24], [-1.5, -0.001, -4], [3, 2, 2], inflate);
  r.box(head, [0, 10], [-2, -3, 0], [1, 1, 2], inflate);
  r.box(head, [6, 10], [1, -3, 0], [1, 1, 2], inflate);
  const body = r.pivot([0, 12, -10], r.parts, [PI / 2, 0, 0]);
  r.box(body, [20, 0], [-2, 3, -8], [4, 16, 6], inflate);
  const tail1 = r.pivot([0, 15, 8], r.parts, [0.9, 0, 0]);
  r.box(tail1, [0, 15], [-0.5, 0, 0], [1, 8, 1], inflate);
  const tail2 = r.pivot([0, 20, 14]);
  r.box(tail2, [4, 15], [-0.5, 0, 0], [1, 8, 1], inflate - 0.02);
  const bl = r.pivot([1.1, 18, 5]), br = r.pivot([-1.1, 18, 5]);
  r.box(bl, [8, 13], [-1, 0, 1], [2, 6, 2], inflate); r.box(br, [8, 13], [-1, 0, 1], [2, 6, 2], inflate);
  const fl = r.pivot([1.2, 14.1, -5]), fr = r.pivot([-1.2, 14.1, -5]);
  r.box(fl, [40, 0], [-1, 0, 0], [2, 10, 2], inflate); r.box(fr, [40, 0], [-1, 0, 0], [2, 10, 2], inflate);
  return { r, head, body, tail1, tail2, bl, br, fl, fr };
}

/**
 * Cat: `view.color` bits 0-3 collar dye, bit 4 tamed, bit 5 sitting, bits 6-9 coat
 * (entities/overworld2/cat.ts). Kittens are the pre-26.1 scaled adult (body 1/2, head 3/4).
 */
export function catAnim(env: EnvUniforms): OwAnim {
  const layers = [catRig(env, 'entity/cat/cat_tabby', 0), catRig(env, 'entity/cat/cat_collar', 0.01)];
  const coatMat = layers[0].r.mat, collarMat = layers[1].r.mat;
  collarMat.transparent = true; collarMat.depthWrite = false;
  const parts = new THREE.Group();
  // AgeableListModel(scaleHead, yHeadOffset 10, zHeadOffset 4): the head sits outside the scaled body
  const roots = layers.map((l) => {
    const headRoot = new THREE.Group(), bodyRoot = new THREE.Group();
    for (const c of [...l.r.parts.children]) (c === l.head ? headRoot : bodyRoot).add(c);
    l.r.parts.add(headRoot, bodyRoot);
    parts.add(l.r.parts);
    return { headRoot, bodyRoot };
  });
  let coat = 0;
  const tint = new THREE.Color();
  return {
    parts, mats: [coatMat, collarMat],
    animate(v, q, headY, headX, age) {
      const c = Math.min(CAT_SKINS.length - 1, (v.color >> 6) & 15);
      if (c !== coat) { coat = c; coatMat.uniforms.uMap.value = sheetTexture(`entity/cat/${CAT_SKINS[c]}`); }
      layers[1].r.parts.visible = (v.color & 16) !== 0;
      collarMat.uniforms.uTint.value.copy(tint.setHex(DYE_RGB[v.color & 15]));
      const sit = (v.color & 32) !== 0;
      const s = q.limbSwing * 0.6662, a = q.limbAmt;
      layers.forEach(({ head, body, tail1, tail2, bl, br, fl, fr }, i) => {
        const { headRoot, bodyRoot } = roots[i];
        if (v.baby) {
          headRoot.scale.setScalar(0.75); headRoot.position.set(0, 7.5, 3);
          bodyRoot.scale.setScalar(0.5); bodyRoot.position.set(0, 12, 0);
        } else {
          headRoot.scale.setScalar(1); headRoot.position.set(0, 0, 0);
          bodyRoot.scale.setScalar(1); bodyRoot.position.set(0, 0, 0);
        }
        head.rotation.set(headX, headY, 0);
        // vanilla OcelotModel walking pose (sneak/sprint variants omitted)
        fr.rotation.x = Math.cos(s) * a; fl.rotation.x = Math.cos(s + 0.3) * a;
        br.rotation.x = Math.cos(s + PI + 0.3) * a; bl.rotation.x = Math.cos(s + PI) * a;
        tail2.rotation.x = 0;
        if (sit) {
          body.rotation.x = PI / 4; body.position.set(0, 8, -5); head.position.set(0, 11.7, -8);
          tail1.position.set(0, 23, 6); tail2.position.set(0, 25, 13.2);
          tail1.rotation.x = 1.7278761; tail2.rotation.x = 2.670354;
          fl.rotation.x = fr.rotation.x = -0.15707964; fl.position.set(1.2, 18.1, -3); fr.position.set(-1.2, 18.1, -3);
          bl.rotation.x = br.rotation.x = -1.5707964; bl.position.set(1.1, 21, 1); br.position.set(-1.1, 21, 1);
        } else {
          body.rotation.x = PI / 2; body.position.set(0, 12, -10); head.position.set(0, 15, -9);
          tail1.position.set(0, 15, 8); tail2.position.set(0, 20, 14);
          tail1.rotation.x = 0.9 - a * 0.3; tail2.rotation.x = Math.sin(age * 0.1) * 0.05;
          fl.position.set(1.2, 14.1, -5); fr.position.set(-1.2, 14.1, -5);
          bl.position.set(1.1, 18, 5); br.position.set(-1.1, 18, 5);
        }
      });
    },
    dispose() { coatMat.dispose(); collarMat.dispose(); },
  };
}

// -------------------------------------------------------------- squid / fish

/** SquidModel (64x32): body + eight tentacles, the tentacles are root children. */
export function squidAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/squid/squid', 64, 32);
  const root = r.pivot([0, 0, 0]);
  const body = r.pivot([0, 8, 0], root);
  r.box(body, [0, 0], [-6, -8, -6], [12, 16, 12]);
  const tents: THREE.Group[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i * PI * 2) / 8;
    const t = r.pivot([Math.cos(a) * 5, 15, Math.sin(a) * 5], root);
    t.rotation.y = (i * PI * -2) / 8 + PI / 2;
    r.box(t, [48, 0], [-1, 0, -1], [2, 18, 2]);
    tents.push(t);
  }
  return r.anim((_v, _q, _hy, _hx, age) => {
    tents.forEach((t, i) => { t.rotation.x = 0.35 + Math.sin(age * 0.25 + i) * 0.35; });
    root.position.y = 4 + Math.sin(age * 0.1) * 0.5;
  });
}

/** CodModel / SalmonModel (32x32). */
export function fishAnim(env: EnvUniforms, kind: 'cod' | 'salmon'): OwAnim {
  const r = new Rig(env, `entity/fish/${kind}`, 32, 32, { doubleSided: true });
  const Q = PI / 4;
  let tailA: THREE.Group, tailB: THREE.Group | null = null;
  let finL: THREE.Group, finR: THREE.Group;
  if (kind === 'cod') {
    r.box(r.pivot([0, 22, 0]), [0, 0], [-1, -2, 0], [2, 4, 7]);
    r.box(r.pivot([0, 22, 0]), [11, 0], [-1, -2, -3], [2, 4, 3]);
    r.box(r.pivot([0, 22, 0]), [0, 0], [-1, -2, -4], [2, 3, 1]);
    finR = r.pivot([-1, 23, 0], r.parts, [0, 0, -Q]); r.box(finR, [22, 1], [-2, 0, -1], [2, 0, 2]);
    finL = r.pivot([1, 23, 0], r.parts, [0, 0, Q]); r.box(finL, [22, 4], [0, 0, -1], [2, 0, 2]);
    tailA = r.pivot([0, 22, 7]); r.box(tailA, [22, 3], [0, -2, 0], [0, 4, 4]);
    r.box(r.pivot([0, 20, 0]), [20, -6], [0, -1, -1], [0, 1, 6]);
  } else {
    r.box(r.pivot([0, 20, 0]), [0, 0], [-1.5, -2.5, 0], [3, 5, 8]);
    const back = r.pivot([0, 20, 8]); tailB = back;
    r.box(back, [0, 13], [-1.5, -2.5, 0], [3, 5, 8]);
    r.box(r.pivot([0, 20, 0]), [22, 0], [-1, -2, -3], [2, 4, 3]);
    tailA = r.pivot([0, 0, 8], back); r.box(tailA, [20, 21], [0, -2.5, 0], [0, 5, 6]);
    r.box(r.pivot([0, 15.5, 5]), [2, 1], [0, 0, 0], [0, 2, 3]);
    r.box(r.pivot([0, 15.5, 7]), [0, 0], [0, 0, 0], [0, 2, 4]);
    finR = r.pivot([-1.5, 21.5, 0], r.parts, [0, 0, -Q]); r.box(finR, [-4, -1], [-2, 0, 0], [2, 0, 2]);
    finL = r.pivot([1.5, 21.5, 0], r.parts, [0, 0, Q]); r.box(finL, [0, -4], [0, 0, 0], [2, 0, 2]);
  }
  return r.anim((v, _q, _hy, _hx, age) => {
    // vanilla: tail fin sweeps -amp * 0.45 * sin(0.6 * age); salmon bends its rear body
    const sw = Math.sin(0.6 * age + v.id);
    if (kind === 'cod') tailA.rotation.y = -0.45 * sw; else { tailB!.rotation.y = -0.25 * sw; tailA.rotation.y = -0.25 * sw; }
    finL.rotation.z = Q + Math.sin(age * 0.5) * 0.1; finR.rotation.z = -Q - Math.sin(age * 0.5) * 0.1;
  });
}

// --------------------------------------------------------------------- bat

/** BatModel (32x32): flat (zero-depth) wing/ear planes, drawn double-sided. */
export function batAnim(env: EnvUniforms): OwAnim {
  const r = new Rig(env, 'entity/bat/bat', 32, 32, { doubleSided: true });
  const body = r.pivot([0, 8, 0]);
  r.box(body, [0, 0], [-1.5, 0, -1], [3, 5, 2]);
  const head = r.pivot([0, 8, 0]);
  r.box(head, [0, 7], [-2, -3, -1], [4, 3, 2]);
  const earR = r.pivot([-1.5, -2, 0], head); r.box(earR, [1, 15], [-2.5, -4, 0], [3, 5, 0]);
  const earL = r.pivot([1.1, -3, 0], head); r.box(earL, [8, 15], [-0.1, -3, 0], [3, 5, 0]);
  const wr = r.pivot([-1.5, 0, 0], body); r.box(wr, [12, 0], [-2, 0, 0], [2, 7, 0]);
  const wr2 = r.pivot([-2, 0, 0], wr); r.box(wr2, [16, 0], [-6, 0, 0], [6, 8, 0]);
  const wl = r.pivot([1.5, 0, 0], body); r.box(wl, [12, 7], [0, 0, 0], [2, 7, 0]);
  const wl2 = r.pivot([2, 0, 0], wl); r.box(wl2, [16, 8], [0, 0, 0], [6, 8, 0]);
  const feet = r.pivot([0, 5, 0], body); r.box(feet, [16, 16], [-1.5, 0, 0], [3, 2, 0]);
  return r.anim((v, _q, headY, headX, age) => {
    if ((v.color & 1) !== 0) {
      // hanging upside down with wings folded (vanilla resting pose)
      body.rotation.x = PI; body.position.y = 8 + 12; head.position.y = 8 + 12; head.rotation.set(PI + 0, headY, 0);
      wr.rotation.y = 1.4; wl.rotation.y = -1.4; wr2.rotation.y = 0.9; wl2.rotation.y = -0.9;
      return;
    }
    const bob = Math.cos(age * 0.1) * 0.8;
    body.rotation.x = 0.6 + Math.cos(age * 0.1) * 0.05;
    body.position.y = 12 + bob; head.position.y = 12 + bob;
    const f = Math.cos(age * 1.3) * PI * 0.25;
    wr.rotation.y = f; wl.rotation.y = -f; wr2.rotation.y = f * 1.2; wl2.rotation.y = -f * 1.2;
    head.rotation.set(headX * 0.5, headY * 0.5, 0);
  });
}
